import { createSlice, PayloadAction, Reducer } from '@reduxjs/toolkit';

import { UserDataType } from 'domain/auth/types';

import { ACCOUNT_SLICE_NAME, ACCOUNT_INITIAL_STATE } from './consts';
import type { AccountState } from './types';
import { fetchAccountData, initializeAccountData, refreshAccountData, fetchCurrentUserData } from './thunks';

const accountSlice = createSlice({
  name: ACCOUNT_SLICE_NAME,
  initialState: ACCOUNT_INITIAL_STATE,
  reducers: {
    selectCompany: (state, action: PayloadAction<string>) => {
      state.currentCompanyId = action.payload;
      state.dataStale = true;
    },

    markDataAsStale: (state) => {
      state.dataStale = true;
    },

    clearAccountData: () => ({ ...ACCOUNT_INITIAL_STATE }),

    updateIsReadyToUseLK: (state, action: PayloadAction<boolean>) => {
      state.isReadyToUseLK = action.payload;
    },

    updateIsWithoutCompanies: (state, action: PayloadAction<boolean>) => {
      state.isWithoutCompanies = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },

    setUserData: (state, action: PayloadAction<UserDataType | undefined>) => {
      state.userData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccountData.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.currentCompanyId = action.payload.currentCompanyId || '';
        state.isBillingEnabled = action.payload.isBillingEnabled;
        state.streamLibraryId = action.payload.streamLibraryId || '';
        state.isReadyToUseLK = action.payload.isReadyToUseLK;
        state.isAuthorized = true;
        state.isWithoutCompanies = action.payload.isWithoutCompanies;
        state.hasActiveContractMigration = action.payload.hasActiveContractMigration;

        state.lastFetchTime = Date.now();
        state.dataStale = false;
        state.loading = false;
      })
      .addCase(fetchAccountData.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch account data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userData = undefined;
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
          state.isWithoutCompanies = false;
        }

        state.loading = false;
      })

      .addCase(initializeAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeAccountData.fulfilled, (state, action) => {
        if (action.payload !== null) {
          state.lastFetchTime = Date.now();
          state.dataStale = false;

          state.userData = action.payload.userData;
          state.currentCompanyId = action.payload.currentCompanyId || '';
          state.isBillingEnabled = action.payload.isBillingEnabled;
          state.streamLibraryId = action.payload.streamLibraryId || '';
          state.isReadyToUseLK = action.payload.isReadyToUseLK;
          state.isAuthorized = true;
          state.isWithoutCompanies = action.payload.isWithoutCompanies;
          state.hasActiveContractMigration = action.payload.hasActiveContractMigration;
        }

        state.loading = false;
      })
      .addCase(initializeAccountData.rejected, (state, action) => {
        state.error = action.payload || 'Failed to initialize account data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userData = undefined;
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
          state.isWithoutCompanies = false;
        }

        state.loading = false;
      })

      .addCase(refreshAccountData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(refreshAccountData.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.currentCompanyId = action.payload.currentCompanyId || '';
        state.isBillingEnabled = action.payload.isBillingEnabled;
        state.streamLibraryId = action.payload.streamLibraryId || '';
        state.isReadyToUseLK = action.payload.isReadyToUseLK;
        state.isAuthorized = true;
        state.isWithoutCompanies = action.payload.isWithoutCompanies;
        state.hasActiveContractMigration = action.payload.hasActiveContractMigration;

        state.lastFetchTime = Date.now();
        state.dataStale = false;
        state.loading = false;
      })
      .addCase(refreshAccountData.rejected, (state, action) => {
        state.error = action.payload || 'Failed to refresh account data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userData = undefined;
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
          state.isWithoutCompanies = false;
        }

        state.loading = false;
      })

      .addCase(fetchCurrentUserData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCurrentUserData.fulfilled, (state, action) => {
        state.userData = action.payload.userData;
        state.currentCompanyId = action.payload.currentCompanyId || '';
        state.isBillingEnabled = action.payload.isBillingEnabled;
        state.streamLibraryId = action.payload.streamLibraryId || '';
        state.isReadyToUseLK = action.payload.isReadyToUseLK;
        state.isAuthorized = true;
        state.isWithoutCompanies = action.payload.isWithoutCompanies;
        state.hasActiveContractMigration = action.payload.hasActiveContractMigration;

        state.lastFetchTime = Date.now();
        state.dataStale = false;
        state.loading = false;
      })
      .addCase(fetchCurrentUserData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch current user data';

        if (action.payload?.includes('Unauthorized') || action.payload?.includes('Authentication')) {
          state.isAuthorized = false;
          state.userData = undefined;
          state.streamLibraryId = '';
          state.isBillingEnabled = false;
          state.isWithoutCompanies = false;
        }

        state.loading = false;
      });
  },
});

export const {
  selectCompany,
  markDataAsStale,
  clearAccountData,
  updateIsReadyToUseLK,
  updateIsWithoutCompanies,
  clearError,
  setLoading,
  setUserData,
} = accountSlice.actions;

export const accountReducer: Reducer<AccountState> = accountSlice.reducer;
